import { useEffect, useCallback, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { RealtimePostgresChangesPayload } from '@supabase/supabase-js';

/**
 * Smart incremental realtime updates - only fetches what changed
 * Minimal egress cost and efficient data updates
 */
export function useSmartRealtime<T extends { id: string }>(
  table: string,
  onInsert?: (newRecord: T) => void,
  onUpdate?: (updatedRecord: T) => void,
  onDelete?: (deletedId: string) => void,
  filter?: { column: string; value: any }
) {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);
  const callbacksRef = useRef({ onInsert, onUpdate, onDelete });

  // Update callbacks ref when they change (without triggering re-subscription)
  useEffect(() => {
    callbacksRef.current = { onInsert, onUpdate, onDelete };
  }, [onInsert, onUpdate, onDelete]);

  useEffect(() => {
    // Create a stable channel name based on table and filter (not timestamp)
    const filterKey = filter ? `${filter.column}_${filter.value}` : 'all';
    const channelName = `${table}_smart_${filterKey}`;

    // Clean up existing channel if it exists
    if (channelRef.current) {
      supabase.removeChannel(channelRef.current);
      channelRef.current = null;
    }

    const channel = supabase.channel(channelName);

    const handleRealtimeChange = (payload: RealtimePostgresChangesPayload<T>) => {
      // Use ref to get latest callbacks without re-subscribing
      const { onInsert, onUpdate, onDelete } = callbacksRef.current;

      switch (payload.eventType) {
        case 'INSERT':
          if (payload.new && onInsert) {
            onInsert(payload.new as T);
          }
          break;
        case 'UPDATE':
          if (payload.new && onUpdate) {
            onUpdate(payload.new as T);
          }
          break;
        case 'DELETE':
          if (payload.old && onDelete) {
            onDelete((payload.old as T).id);
          }
          break;
      }
    };

    if (filter) {
      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table,
          filter: `${filter.column}=eq.${filter.value}`
        },
        handleRealtimeChange
      );
    } else {
      channel.on(
        'postgres_changes',
        {
          event: '*',
          schema: 'public',
          table: table,
        },
        handleRealtimeChange
      );
    }

    channel.subscribe((status) => {
      if (status === 'SUBSCRIBED') {
        console.log(`✅ Smart realtime subscribed to ${table}${filter ? ` (${filter.column}=${filter.value})` : ''}`);
      }
    });

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [table, filter?.column, filter?.value]); // Removed handleRealtimeChange from dependencies
}

/**
 * Hook for broadcast messages across all users (no database query needed)
 * Perfect for coordinating UI updates without any egress cost
 */
export function useBroadcastChannel(
  channelName: string,
  eventName: string,
  onMessage: (payload: any) => void
) {
  const channelRef = useRef<ReturnType<typeof supabase.channel> | null>(null);

  useEffect(() => {
    const channel = supabase.channel(channelName);

    channel
      .on('broadcast', { event: eventName }, ({ payload }) => {
        console.log(`📡 Received broadcast on ${channelName}:`, payload);
        onMessage(payload);
      })
      .subscribe();

    channelRef.current = channel;

    return () => {
      if (channelRef.current) {
        supabase.removeChannel(channelRef.current);
        channelRef.current = null;
      }
    };
  }, [channelName, eventName, onMessage]);

  const broadcast = useCallback(
    (payload: any) => {
      if (channelRef.current) {
        channelRef.current.send({
          type: 'broadcast',
          event: eventName,
          payload,
        });
        console.log(`📤 Broadcast sent on ${channelName}:`, payload);
      }
    },
    [channelName, eventName]
  );

  return { broadcast };
}
